import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Zap, Droplets, Clock, ArrowRight, ShieldCheck, Shield, TrendingDown, Package, Plug, Target, Quote, Gauge } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/shared/ProductCard';
import { SEO } from '../components/shared/SEO';
export function HomePage() {
  return <div className="flex flex-col" data-id="element-178">
      <SEO title="Precision Pro Sprayers | ForceX Plug-and-Play Electric Sprayers" description="Professional plug-and-play electric sprayers built for contractors. Eliminate pouring, reduce spills, and finish jobs faster with ForceX sprayer kits." data-id="element-179" />

      {/* Hero Section */}
      <section className="relative bg-brand-dark text-white py-20 lg:py-32 overflow-hidden" data-id="element-180">
        <div className="absolute inset-0 bg-black/75 z-10" data-id="element-181"></div>
        <div className="absolute inset-0 z-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/mnYnLdKB4CX3ZrYpuepiJd/hero3.png)'
      }} data-id="element-182"></div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-183">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight uppercase" data-id="element-184">
            Spray With Precision
          </h1>
          <p className="text-2xl md:text-3xl text-brand-yellow font-bold mb-4 uppercase" data-id="element-185">
            Ditch Your Pump Sprayer
          </p>
          <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed uppercase font-bold" data-id="element-186">
            Finish Your Job In Half The Time.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-187">
            <a href="#kits" data-id="element-188">
              <Button variant="primary" size="lg" data-id="element-189">
                View Kits
              </Button>
            </a>
            <Link to="/contact" data-id="element-190">
              <Button variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-brand-dark" data-id="element-191">
                Call Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Product Preview */}
      <section id="kits" className="py-20 bg-brand-light scroll-mt-20" data-id="element-192">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-193">
          <div className="text-center mb-12" data-id="element-194">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-195">
              Professional Grade Kits
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed" data-id="element-196">
              Professional-grade electric sprayer systems designed for
              contractors to apply chemicals faster, safer, and more
              consistently—eliminating manual pumping, heavy lifting, and
              downtime in attics, crawlspaces, and tight work areas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-197">
            <ProductCard title="ForceX – Complete Kit" description="Out of the box, ready to work. Includes enclosed system housing for maximum efficiency and cleanliness." imageLabel="[Product Image - Complete Kit]" linkTo="/products/forcex-complete" price="Best Value" imageUrl="https://cdn.magicpatterns.com/uploads/nYew1mr6dTPANQzToCfw2J/complete.png" data-id="element-198" />
            <ProductCard title="ForceX – DIY Kit" description="Build it your way. The flexible solution for contractors who want to customize your setup." imageLabel="[Product Image - DIY Kit]" linkTo="/products/forcex-diy" price="Intro Pricing Available" imageUrl="https://cdn.magicpatterns.com/uploads/76x41AeJfoY4b3NAgLoMt1/DIY_KIt.png" data-id="element-199" />
          </div>
        </div>
      </section>

      {/* Unified Benefits Section */}
      <section className="py-20 bg-white" data-id="element-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-201">
          <div className="text-center mb-12" data-id="element-202">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-203">
              Why Contractors Choose Precision Pro Sprayers
            </h2>
            <p className="text-xl text-gray-600" data-id="element-204">
              Designed to eliminate hauling, reduce downtime, and speed up every
              job.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-205">
            {/* Top Row - Outcome-Focused */}
            <div className="flex items-start space-x-4 p-6 bg-brand-light rounded-lg border border-gray-200" data-id="element-206">
              <div className="flex-shrink-0" data-id="element-207">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center" data-id="element-208">
                  <Shield className="h-6 w-6 text-brand-yellow" data-id="element-209" />
                </div>
              </div>
              <div data-id="element-210">
                <h3 className="text-xl font-bold text-brand-dark mb-2" data-id="element-211">
                  Stay Safe
                </h3>
                <p className="text-gray-600" data-id="element-212">
                  Keep chemicals on the ground and reduce exposure in tight
                  spaces.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4 p-6 bg-brand-light rounded-lg border border-gray-200" data-id="element-213">
              <div className="flex-shrink-0" data-id="element-214">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center" data-id="element-215">
                  <Clock className="h-6 w-6 text-brand-yellow" data-id="element-216" />
                </div>
              </div>
              <div data-id="element-217">
                <h3 className="text-xl font-bold text-brand-dark mb-2" data-id="element-218">
                  Save Time
                </h3>
                <p className="text-gray-600" data-id="element-219">
                  Faster setup, no pumping, no refilling, no downtime.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4 p-6 bg-brand-light rounded-lg border border-gray-200" data-id="element-220">
              <div className="flex-shrink-0" data-id="element-221">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center" data-id="element-222">
                  <TrendingDown className="h-6 w-6 text-brand-yellow" data-id="element-223" />
                </div>
              </div>
              <div data-id="element-224">
                <h3 className="text-xl font-bold text-brand-dark mb-2" data-id="element-225">
                  Save Money
                </h3>
                <p className="text-gray-600" data-id="element-226">
                  Fewer spills, less waste, and faster job completion.
                </p>
              </div>
            </div>

            {/* Bottom Row - Feature-Focused */}
            <div className="p-6 rounded-xl bg-brand-light border border-gray-100 text-center" data-id="element-227">
              <div className="w-16 h-16 bg-brand-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-228">
                <Zap className="h-8 w-8 text-brand-yellow" data-id="element-229" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-230">
                Plug-and-Play Electric
              </h3>
              <p className="text-gray-600" data-id="element-231">
                No batteries to charge or fail. Consistent power all day long.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-brand-light border border-gray-100 text-center" data-id="element-232">
              <div className="w-16 h-16 bg-brand-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-233">
                <Droplets className="h-8 w-8 text-brand-yellow" data-id="element-234" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-235">
                75-Foot Hose
              </h3>
              <p className="text-gray-600" data-id="element-236">
                Reach further without moving your setup. Maximum mobility in
                tight spaces.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-brand-light border border-gray-100 text-center" data-id="element-237">
              <div className="w-16 h-16 bg-brand-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-238">
                <Clock className="h-8 w-8 text-brand-yellow" data-id="element-239" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-240">
                Faster Jobs
              </h3>
              <p className="text-gray-600" data-id="element-241">
                Less lifting. Less spills. Get in, spray, and get out faster.
              </p>
            </div>
          </div>

          {/* Automatic Pressure Control Section */}
          <div className="bg-brand-dark text-white p-8 rounded-xl border border-gray-700" data-id="element-242">
            <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-243">
              <div className="flex-shrink-0" data-id="element-244">
                <div className="w-20 h-20 bg-brand-yellow/20 rounded-full flex items-center justify-center" data-id="element-245">
                  <Gauge className="h-10 w-10 text-brand-yellow" data-id="element-246" />
                </div>
              </div>
              <div className="text-left" data-id="element-247">
                <h3 className="text-2xl font-bold text-brand-yellow mb-3" data-id="element-248">
                  Automatic Pressure Control (60 PSI)
                </h3>
                <p className="text-gray-300 leading-relaxed max-w-3xl" data-id="element-249">
                  The ForceX system automatically builds pressure up to 60 PSI
                  and then shuts off. When the spray trigger is squeezed,
                  pressure is released and the pump automatically reactivates to
                  restore pressure on demand. This ensures consistent spray
                  performance, prevents over-pressurization, and reduces wear on
                  the system.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 relative overflow-hidden" data-id="element-250">
        {/* Video Background */}
        <div className="absolute inset-0 z-0" data-id="element-251">
          <div className="absolute inset-0 bg-white/40 z-10" data-id="element-252"></div>
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-253">
            <source src="https://cdn.magicpatterns.com/uploads/mDGvpjFBDgEvF12sEZF8rw/SprayClip.mp4" type="video/mp4" data-id="element-254" />
            <source src="https://cdn.magicpatterns.com/uploads/mDGvpjFBDgEvF12sEZF8rw/SprayClip.mov" type="video/quicktime" data-id="element-255" />
            <source src="https://cdn.magicpatterns.com/uploads/mDGvpjFBDgEvF12sEZF8rw/SprayClip.undefined" type="video/mp4" data-id="element-256" />
          </video>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20" data-id="element-257">
          <div className="text-center mb-16" data-id="element-258">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-259">
              How It Works
            </h2>
            <p className="text-xl text-gray-800 font-medium" data-id="element-260">
              Three simple steps to faster, safer spraying.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto" data-id="element-261">
            <div className="text-center" data-id="element-262">
              <div className="w-20 h-20 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-263">
                <Package className="h-10 w-10 text-brand-dark" data-id="element-264" />
              </div>
              <div className="text-brand-yellow font-bold text-lg mb-3 drop-shadow-sm" data-id="element-265">
                Step 1
              </div>
              <h3 className="text-2xl font-bold text-brand-dark mb-3" data-id="element-266">
                Order Your Kit
              </h3>
              <p className="text-gray-800 font-medium" data-id="element-267">
                Choose the DIY or Complete ForceX system that fits your
                workflow.
              </p>
            </div>

            <div className="text-center" data-id="element-268">
              <div className="w-20 h-20 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-269">
                <Plug className="h-10 w-10 text-brand-dark" data-id="element-270" />
              </div>
              <div className="text-brand-yellow font-bold text-lg mb-3 drop-shadow-sm" data-id="element-271">
                Step 2
              </div>
              <h3 className="text-2xl font-bold text-brand-dark mb-3" data-id="element-272">
                Plug It In
              </h3>
              <p className="text-gray-800 font-medium" data-id="element-273">
                No batteries, no charging. Just reliable, corded power.
              </p>
            </div>

            <div className="text-center" data-id="element-274">
              <div className="w-20 h-20 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-275">
                <Target className="h-10 w-10 text-brand-dark" data-id="element-276" />
              </div>
              <div className="text-brand-yellow font-bold text-lg mb-3 drop-shadow-sm" data-id="element-277">
                Step 3
              </div>
              <h3 className="text-2xl font-bold text-brand-dark mb-3" data-id="element-278">
                Spray with Precision
              </h3>
              <p className="text-gray-800 font-medium" data-id="element-279">
                Drop the intake line into your container and get to work.
              </p>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-280">
            <a href="#kits" data-id="element-281">
              <Button variant="primary" size="lg" data-id="element-282">
                View Kits
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="py-20 bg-white" data-id="element-283">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-284">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-285">
            <div data-id="element-286">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-8" data-id="element-287">
                Stop Hauling. Start Spraying.
              </h2>
              <div className="space-y-8" data-id="element-288">
                <div className="border-l-4 border-red-500 pl-6" data-id="element-289">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-290">
                    The Old Way
                  </h3>
                  <ul className="space-y-2 text-gray-600" data-id="element-291">
                    <li className="flex items-start" data-id="element-292">
                      <span className="mr-2 text-red-500" data-id="element-293">✕</span> Carrying
                      heavy chemicals into attics & crawlspaces
                    </li>
                    <li className="flex items-start" data-id="element-294">
                      <span className="mr-2 text-red-500" data-id="element-295">✕</span> Pouring into
                      backpacks or small tanks
                    </li>
                    <li className="flex items-start" data-id="element-296">
                      <span className="mr-2 text-red-500" data-id="element-297">✕</span> Battery
                      failures and expensive downtime
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-brand-yellow pl-6" data-id="element-298">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-299">
                    The ForceX Way
                  </h3>
                  <ul className="space-y-2 text-gray-600" data-id="element-300">
                    <li className="flex items-start" data-id="element-301">
                      <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-2 mt-0.5" data-id="element-302" />{' '}
                      External container siphon - leave the heavy lifting behind
                    </li>
                    <li className="flex items-start" data-id="element-303">
                      <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-2 mt-0.5" data-id="element-304" />{' '}
                      Corded reliability - never run out of power
                    </li>
                    <li className="flex items-start" data-id="element-305">
                      <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-2 mt-0.5" data-id="element-306" />{' '}
                      Fast setup, controlled spray, automatic shutoff
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8" data-id="element-307">
                <a href="#kits" data-id="element-308">
                  <Button variant="primary" data-id="element-309">Upgrade Your Setup</Button>
                </a>
              </div>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden" data-id="element-310">
              <img src="https://cdn.magicpatterns.com/uploads/o6LcpLcnE3tYBY2tteo4MD/old-way.png" alt="Contractor pouring chemicals into backpack sprayer" className="w-full h-full object-cover" style={{
              objectPosition: '65% center',
              filter: 'brightness(0.88) saturate(0.85)'
            }} data-id="element-311" />
              <div className="absolute inset-0 bg-black/12" data-id="element-312"></div>
              <div className="absolute bottom-4 right-4 text-white/40 text-xs uppercase tracking-wider font-bold" data-id="element-313">
                The Old Way
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-brand-light" data-id="element-314">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-315">
          <div className="text-center mb-16" data-id="element-316">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-317">
              Trusted by Professionals
            </h2>
            <p className="text-xl text-gray-600" data-id="element-318">
              Real feedback from contractors in the field.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-319">
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm" data-id="element-320">
              <Quote className="h-10 w-10 text-brand-yellow mb-4" data-id="element-321" />
              <p className="text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-322">
                "Made my attic work faster and safer. No more hauling chemicals
                up ladders."
              </p>
              <div className="flex items-center" data-id="element-323">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center mr-4" data-id="element-324">
                  <span className="text-brand-dark font-bold text-lg" data-id="element-325">DC</span>
                </div>
                <div data-id="element-326">
                  <div className="font-bold text-brand-dark" data-id="element-327">Dustin C.</div>
                  <div className="text-gray-500 text-sm" data-id="element-328">Contractor</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm" data-id="element-329">
              <Quote className="h-10 w-10 text-brand-yellow mb-4" data-id="element-330" />
              <p className="text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-331">
                "This system just works. Plug it in and spray."
              </p>
              <div className="flex items-center" data-id="element-332">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center mr-4" data-id="element-333">
                  <ShieldCheck className="h-6 w-6 text-brand-yellow" data-id="element-334" />
                </div>
                <div data-id="element-335">
                  <div className="font-bold text-brand-dark" data-id="element-336">
                    Restoration Contractor
                  </div>
                  <div className="text-gray-500 text-sm" data-id="element-337">Verified Buyer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-white" data-id="element-338">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-339">
          <div className="text-center mb-12" data-id="element-340">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-341">
              Who It's For
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-342">
              Used by professionals in:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto" data-id="element-343">
            {['Mold remediation', 'Disinfecting and sanitation', 'Crawlspaces and attics', 'Facilities maintenance', 'Property management', 'Restoration contractors'].map(industry => <div key={industry} className="flex items-center p-4 bg-brand-light rounded-lg border border-gray-200" data-id="element-344">
                <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-3 flex-shrink-0" data-id="element-345" />
                <span className="text-gray-700 font-medium" data-id="element-346">{industry}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-brand-dark text-white" data-id="element-347">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-348">
          <div className="text-center mb-16" data-id="element-349">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-350">
              Built For Your Industry
            </h2>
            <p className="text-xl text-gray-400" data-id="element-351">
              Trusted by professionals across multiple sectors.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-352">
            {['Mold Remediation', 'Disinfecting & Antimicrobials', 'Facilities Maintenance', 'Contractors', 'Municipal / Commercial', 'Residential Professionals'].map(industry => <div key={industry} className="bg-gray-800 p-6 rounded-lg text-center hover:bg-gray-700 transition-colors border border-gray-700" data-id="element-353">
                <ShieldCheck className="h-8 w-8 text-brand-yellow mx-auto mb-3" data-id="element-354" />
                <h3 className="font-bold text-lg" data-id="element-355">{industry}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-brand-yellow" data-id="element-356">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-357">
          <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-6" data-id="element-358">
            Built for Contractors Who Hate Wasted Time
          </h2>
          <p className="text-xl text-gray-800 mb-10 leading-relaxed" data-id="element-359">
            Precision Pro Sprayers are designed to eliminate unnecessary steps,
            reduce exposure, and make spraying faster and safer in tight spaces.
          </p>
          <a href="#kits" data-id="element-360">
            <Button variant="primary" size="lg" className="bg-brand-dark text-white hover:bg-gray-800" data-id="element-361">
              Shop Sprayers
              <ArrowRight className="ml-2 h-5 w-5" data-id="element-362" />
            </Button>
          </a>
        </div>
      </section>
    </div>;
}